import React, {
  useCallback,
  useState,
  useRef,
  useContext,
} from 'react';
import { connect } from 'react-redux';
import * as PropTypes from 'prop-types';
import { Link } from 'react-router-dom';
import ReCAPTCHA from 'react-google-recaptcha';
import './Registration.css';
import {
  Form, Icon, Input, Button, Row, Checkbox, Typography, Divider,
} from 'antd';
import { FormattedMessage, useIntl } from 'react-intl';
import ViaValidationAlert from '../../../components/fields/ViaValidationAlert';
import LocaleSelector from '../../../components/translations/LocaleSelector';
import routeLocations from '../../../utility/route-locations';
import { ViaLocaleContext } from '../../../contexts/ViaLanguageProvider';
import { requestRegisterUser } from '../../../state/ui/login/registration/registrationUiActions';
import { selectRegisterRequest } from '../../../state/ui/login/registration/registrationUiReducer';
import { ViaLogo } from '../../../components/logo/ViaLogo';

const { Text } = Typography;


const createUsernameField = (form, intl) => form.getFieldDecorator('username', {
  rules: [
    { required: true, message: intl.formatMessage({ id: 'validationEmail' }) },
    { type: 'email', message: intl.formatMessage({ id: 'invalidEmail' }) },
  ],
})(
  <Input
    prefix={<Icon type="user" style={{ color: 'rgba(0,0,0,0.25)' }} />}
    type="email"
    placeholder={intl.formatMessage({ id: 'email' })}
    maxLength={80}
  />,
);

const createDisplayNameField = (form, intl) => form.getFieldDecorator('displayName', {
  rules: [
    { required: true, message: intl.formatMessage({ id: 'validationDisplayName' }) },
  ],
})(
  <Input
    prefix={<Icon type="idcard" style={{ color: 'rgba(0,0,0,0.25)' }} />}
    type="username"
    placeholder={intl.formatMessage({ id: 'displayName' })}
    maxLength={80}
  />,
);


// The root registration component.
// Checks if the user is done with authentication and redirects them.
// Otherwise displays the registration form.
function Registration({
  form, loading, registerError, registerUser,
}) {
  const intl = useIntl();

  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [acceptedPrivacy, setAcceptedPrivacy] = useState(false);
  const [acceptedEndUser, setAcceptedEndUser] = useState(false);
  const [checkBoxError, setCheckBoxError] = useState(null);
  const recaptchaRef = useRef();

  const { appLocale } = useContext(ViaLocaleContext);

  const [textVisible, setTextVisible] = useState(false);
  const suffixIcon = textVisible
    ? (<Icon type="eye-invisible" onClick={() => setTextVisible(false)} style={{ color: 'rgba(0,0,0,.5)' }} />)
    : (<Icon type="eye" onClick={() => setTextVisible(true)} style={{ color: 'rgba(0,0,0,.5)' }} />);
  const fieldType = textVisible ? 'text' : 'password';

  const allDocumentsAccepted = acceptedTerms && acceptedPrivacy && acceptedEndUser;

  const onChange = useCallback((token) => {
    // During the Recaptcha, a user can still modify the fields, so we need to re-validate.
    form.validateFields((err, values) => {
      if (!err) {
        recaptchaRef.current.reset();

        registerUser(values.username, values.password, values.displayName, appLocale.code, token);
        // clear The checkbox alert since they have clearly filed in all three to get here
        setCheckBoxError(null);
      }
    });
  }, [form, registerUser, appLocale]);

  const preSubmit = (useCallback((e) => {
    e.preventDefault();
    form.validateFields((err) => {
      if (!err) {
        if (!allDocumentsAccepted) {
          setCheckBoxError({
            errorMessage: intl.formatMessage({ id: 'termsAccept' }),
            errors: [intl.formatMessage({ id: 'termsAccept' })],
            code: 400,
          });
          return;
        }
        recaptchaRef.current.execute();
      }
    });
  }, [form, allDocumentsAccepted, intl]));

  const createCheckBoxField = (messageId, link, setter) => (
    <Checkbox className="registration-checkbox-row" onChange={e => setter(e.target.checked)}>
      <FormattedMessage id="iAgree" />
      <Link to={link} target="_blank"><FormattedMessage id={messageId} /></Link>
    </Checkbox>
  );

  return (
    <div className="app-registration-layout">
      <Row type="flex" align="middle" justify="center">
        <div className="app-registration-content">
          <Row type="flex" justify="center">
            <ViaLogo href={routeLocations.landing} className="app-registration-logo" />
          </Row>
          <Row type="flex" justify="center">
            <Text className="registration-text"><FormattedMessage id="registrationWelcome" /></Text>
            <ReCAPTCHA
              ref={recaptchaRef}
              size="invisible"
              sitekey={process.env.REACT_APP_RECAPTCHA}
              onChange={onChange}
            />
          </Row>
          <Row type="flex" justify="center">
            <ViaValidationAlert validationError={registerError} closable className="registration-checkbox-alert" />
          </Row>
          <Row>
            <Form onSubmit={e => preSubmit(e)} className="app-login-form">
              <Form.Item>
                { createUsernameField(form, intl) }
              </Form.Item>
              <Form.Item>
                {
                  form.getFieldDecorator('password', {
                    rules: [
                      { required: true, message: intl.formatMessage({ id: 'validationPassword' }) },
                    ],
                  })(
                    <Input
                      prefix={<Icon type="lock" style={{ color: 'rgba(0,0,0,.25)' }} />}
                      type={fieldType}
                      placeholder={intl.formatMessage({ id: 'password' })}
                      maxLength={80}
                      suffix={suffixIcon}
                    />,
                  )
                }
              </Form.Item>
              <Form.Item>
                { createDisplayNameField(form, intl) }
              </Form.Item>
              <ViaValidationAlert validationError={checkBoxError} className="registration-checkbox-alert" />
              <Row>
                { createCheckBoxField('termsOfUse', '/legal-documents/terms-of-use', setAcceptedTerms, 'terms-of-use')}
              </Row>
              <Row>
                { createCheckBoxField('privacyPolicy', '/legal-documents/privacy-policy', setAcceptedPrivacy, 'privacy-policy')}
              </Row>
              <Row>
                { createCheckBoxField('endUserLicence', '/legal-documents/end-user-licence-agreement', setAcceptedEndUser, 'end-user-licence')}
              </Row>
              <Form.Item>
                <Button
                  type="primary"
                  block
                  htmlType="submit"
                  loading={loading}
                  className="app-registration-button"
                >
                  <FormattedMessage id="register" />
                </Button>
              </Form.Item>
            </Form>
          </Row>
          <Row type="flex" justify="center">
            <LocaleSelector />
          </Row>
          <Divider />
          <Row type="flex" justify="center">
            <Link className="login-form-sign-up" to={routeLocations.login}><FormattedMessage id="registrationlogin" /></Link>
          </Row>
        </div>
      </Row>
    </div>
  );
}


Registration.propTypes = {
  form: PropTypes.shape({
    validateFields: PropTypes.func.isRequired,
    getFieldDecorator: PropTypes.func.isRequired,
  }).isRequired,
  registerError: PropTypes.shape({
    errorMessage: PropTypes.string.isRequired,
    errors: PropTypes.array.isRequired,
  }),
  registerUser: PropTypes.func.isRequired,
  loading: PropTypes.bool,
};

Registration.defaultProps = {
  registerError: null,
  loading: false,
};

const mapStateToProps = (state) => {
  const registerRequest = selectRegisterRequest(state);

  return {
    loading: registerRequest ? registerRequest.isLoading : false,
    registerError: registerRequest ? registerRequest.error : null,
  };
};

const mapDispatchToProps = dispatch => ({
  registerUser: (username, password, displayName, locale, reCaptcha) => dispatch(
    requestRegisterUser(username, password, displayName, locale, reCaptcha),
  ),
});

export default connect(mapStateToProps, mapDispatchToProps)(Form.create({ name: 'main_signup' })(Registration));
